import { useTitle } from 'components/utilities/hooks/useTitle';
import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  Input,
  Paper,
  Typography,
} from '@mui/material';
import { loginAPIUser } from 'services/resources/auth';
import { Link, Redirect } from 'react-router-dom';
import {
  RESET_PASSWORD_URL,
  SIGNUP_URL,
  TEAM_OVERVIEW_URL,
} from 'constants/urls';
import { loginWithAuth0, submitDemoLogin } from 'utilities/auth';
import { DEMO_USER_EMAIL } from 'constants/emails';
import { useAppContext } from 'services/context';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateIsStaff,
  userDetailsAuthTokenSelector,
} from 'store/slices/userDetailsSlice';
import { useLazyGetUserDetailsQuery } from 'store/apis/userDetailsApi';
import { KEYS } from 'constants/keyboard';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useLoginPageStyles } from 'components/Login/styles';
import { getLocalToken } from 'utilities/token';
import { setNotificationsCountForTeamOverview } from 'components/TeamOverview/utilities';

export const LoginPageNew = ({ location, history, isDemo }) => {
  useTitle('HistoWiz - Login');
  const { classes } = useLoginPageStyles();
  const dispatch = useDispatch();
  const { updateIsLoggedIn } = useAppContext();
  const [getUserDetails] = useLazyGetUserDetailsQuery();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { showError } = useSnackbar();

  const jwtToken = useSelector(userDetailsAuthTokenSelector);
  const localToken = getLocalToken();

  const updateStaff = (value) => {
    dispatch(updateIsStaff(value));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.hash);
    const error = params.get('#error');
    const errorDescription = params.get('error_description');
    if (error) {
      showError(`Error: ${error}\nDescription: ${errorDescription}`, '', {
        autoHideDuration: 10000,
      });
      setIsLoading(false);

      return;
    }
    setIsLoading(!!(location.pathname === '/' && location.hash));
  }, [location.pathname]);

  useEffect(() => {
    document.title = 'HistoWiz - Login';
    if (isDemo) {
      setNotificationsCountForTeamOverview();
      submitDemoLogin({ updateStaff, getUserDetails, setRedirectToReferrer });
    }
    if (localToken || jwtToken) {
      setNotificationsCountForTeamOverview();
      history.push(TEAM_OVERVIEW_URL);
    }
  }, [jwtToken, localToken]);

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmailError(false);
    setEmail(event.target.value);
  };

  const submitGuestSignin = () => {
    const postParams = {
      email: DEMO_USER_EMAIL,
      password: 'tree-swift-triangle',
    };

    loginAPIUser(postParams, updateStaff).then(() => {
      getUserDetails();
      updateIsLoggedIn();
      setRedirectToReferrer(true);
    });
  };

  const submitAuth0Signin = async () => {
    setIsLoading(true);
    try {
      await loginWithAuth0(email, password);
      getUserDetails();
    } catch (e) {
      showError(e.description);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === KEYS.ENTER) {
      submitAuth0Signin();
    }
  };

  const disabled = !(email && password) || emailError;
  const { from } = location.state || {
    from: { pathname: TEAM_OVERVIEW_URL },
  };

  if (redirectToReferrer === true) {
    return <Redirect to={from} />;
  }

  return (
    <Paper
      className={`${classes.paperContainer} ${
        isLoading ? classes.loadingState : {}
      }`}
    >
      {isLoading && <CircularProgress className={classes.spinner} />}
      <Grid
        className={classes.fullHeight}
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={0}
      >
        <Grid item xs={12} sm={9} md={8} lg={6} xl={6}>
          <Paper>
            <Paper className={classes.loginHeader}>
              <Typography align="center" variant="h5" color="inherit">
                Login
              </Typography>
            </Paper>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={2}
            >
              <form className={classes.container} noValidate autoComplete="off">
                <Input
                  placeholder="Email ..."
                  className={classes.input}
                  value={email}
                  type="email"
                  onChange={handleEmailChange}
                  error={emailError}
                  autoComplete="username"
                  onKeyPress={handleKeyPress}
                />
                <Input
                  placeholder="Password ..."
                  className={classes.input}
                  value={password}
                  type="password"
                  id="standard-password-input"
                  autoComplete="current-password"
                  onChange={handlePasswordChange}
                  onKeyPress={handleKeyPress}
                />
                <div className={classes.signinButton}>
                  <Grid container justifyContent="space-between" spacing={1}>
                    <Grid item>
                      <Link to={SIGNUP_URL} className={classes.signUpLink}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth={false}
                          disabled={isLoading}
                        >
                          Register?
                        </Button>
                      </Link>
                      <Link
                        to={RESET_PASSWORD_URL}
                        className={classes.resetPasswordLink}
                      >
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth={false}
                          disabled={isLoading}
                        >
                          Forgot Pass?
                        </Button>
                      </Link>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth={false}
                        onClick={submitGuestSignin}
                        disabled={isLoading}
                      >
                        Guest Access
                      </Button>
                      <Button
                        className={classes.signInRightButton}
                        variant="contained"
                        color="primary"
                        disabled={disabled || isLoading}
                        onClick={submitAuth0Signin}
                      >
                        Sign In (Auth0)
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </form>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};
